import { useState } from "react";
import api from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { PageHeader, Panel, Loading, Tag } from "@/components/common";
import { PenNib, Sparkle, Spinner, GraduationCap, SmileyWink, Compass, FilmSlate, VideoCamera, MusicNote, Recycle } from "@phosphor-icons/react";
import { toast } from "sonner";

const AGES = ["2-4", "3-6", "4-7", "5-8"];
const THEMES = ["Colours", "Numbers", "Animals", "Friendship", "Sharing", "Nature", "Cleaning", "Kindness", "Problem solving"];

export default function StoryAI() {
  const { data: existing, loading } = useFetch("/stories");
  const [idea, setIdea] = useState("");
  const [age, setAge] = useState("3-6");
  const [theme, setTheme] = useState("Friendship");
  const [busy, setBusy] = useState(false);
  const [concepts, setConcepts] = useState([]);
  const [busyEp, setBusyEp] = useState(false);
  const [episode, setEpisode] = useState(null);
  const [instruction, setInstruction] = useState("Use my storage first. Reuse existing PinPilot characters, environments, voices, music and SFX. Build a moving visual story with a 2-second hook, minimal dialogue and no static talking heads. Generate as little new material as possible. Do not use premium video. Maximum new AI budget £5.");
  const [planning, setPlanning] = useState(false);
  const [plan, setPlan] = useState(null);
  const [executing, setExecuting] = useState(false);

  const planStorageFirst = async () => {
    if (!idea.trim()) { toast.error("Describe the episode you want"); return; }
    setPlanning(true); setPlan(null);
    try {
      const { data } = await api.post("/production/plan", { data: { brief: idea, instruction, minutes: 10 } });
      const jobId = data.job_id; const started = Date.now();
      const poll = async () => {
        const { data: job } = await api.get(`/production/plan-job/${jobId}`);
        if (job.status === "done" && job.plan) { setPlan(job.plan); setPlanning(false); toast.success("Storage-first plan ready"); }
        else if (job.status === "error") { setPlanning(false); toast.error(job.error || "Planning failed"); }
        else if (Date.now() - started > 180000) { setPlanning(false); toast.error("Planning timed out"); }
        else setTimeout(poll, 3500);
      };
      setTimeout(poll, 3500);
    } catch { setPlanning(false); toast.error("Planning failed"); }
  };

  const executePlan = async () => {
    if (!plan) return;
    setExecuting(true);
    try {
      const { data } = await api.post("/production/execute", { data: { plan } });
      toast.success(`Episode "${data.title}" created — reused ${data.actually_reused}, free/local ${data.free_local_new}, ${data.paid_pending_needs_approval} paid item(s) pending approval`);
    } catch { toast.error("Execute failed"); }
    finally { setExecuting(false); }
  };

  const generate = async () => {
    if (!idea.trim()) { toast.error("Enter a story idea"); return; }
    setBusy(true); setConcepts([]);
    try {
      const { data } = await api.post("/stories/generate", { text: `Idea: ${idea}. Target age ${age}. Educational theme: ${theme}.` });
      if (data.concepts?.length) { setConcepts(data.concepts); toast.success("Generated 3 story concepts"); }
      else toast.error("Could not parse concepts, try again");
    } catch { toast.error("Story generation failed"); }
    finally { setBusy(false); }
  };

  const generateEpisode = async () => {
    if (!idea.trim()) { toast.error("Describe your episode idea (any language)"); return; }
    setBusyEp(true); setEpisode(null);
    try {
      const { data } = await api.post("/episodes/generate", { idea: `${idea}. Target age ${age}. Theme: ${theme}.`, minutes: 10 });
      const jobId = data.job_id;
      const started = Date.now();
      const poll = async () => {
        const { data: job } = await api.get(`/episodes/job/${jobId}`);
        if (job.status === "done" && job.episode?.scenes?.length) {
          setEpisode(job.episode); setBusyEp(false);
          toast.success("Full episode generated & saved to Episode Studio");
        } else if (job.status === "error") {
          setBusyEp(false); toast.error("Episode generation failed");
        } else if (Date.now() - started > 210000) {
          setBusyEp(false); toast.error("Episode generation timed out");
        } else {
          setTimeout(poll, 3500);
        }
      };
      setTimeout(poll, 3500);
    } catch { setBusyEp(false); toast.error("Episode generation failed"); }
  };

  return (
    <div>
      <PageHeader icon={PenNib} title="Story AI"
        subtitle="Enter a simple idea. Studio AI generates full story concepts — title, synopsis, learning objective and story arc — using real AI." />

      <Panel className="mb-8">
        <input data-testid="story-idea" value={idea} onChange={(e) => setIdea(e.target.value)}
          placeholder="e.g. The wizard and horse race to deliver a parcel before the city gate closes"
          className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3 text-white outline-none focus:border-[#00E5FF]/50 mb-4" />
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <div className="text-xs text-zinc-500 mb-2">Target age</div>
            <div className="flex flex-wrap gap-2">
              {AGES.map((a) => (
                <button key={a} data-testid={`age-${a}`} onClick={() => setAge(a)}
                  className={`rounded-full px-3 py-1.5 text-sm border transition-colors ${age === a ? "border-[#00E5FF]/60 bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>{a}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs text-zinc-500 mb-2">Educational theme</div>
            <div className="flex flex-wrap gap-2">
              {THEMES.slice(0, 6).map((t) => (
                <button key={t} onClick={() => setTheme(t)}
                  className={`rounded-full px-3 py-1.5 text-sm border transition-colors ${theme === t ? "border-[#FF007F]/60 bg-[#FF007F]/10 text-[#FF007F]" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>{t}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button data-testid="story-generate" onClick={generate} disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-5 py-2.5 font-semibold text-white hover:border-[#00E5FF]/50 transition-colors disabled:opacity-60">
            {busy ? <Spinner size={18} className="animate-spin" /> : <Sparkle size={18} weight="fill" className="text-[#00E5FF]" />}
            {busy ? "Writing concepts…" : "Generate 3 Concepts"}
          </button>
          <button data-testid="episode-generate" onClick={generateEpisode} disabled={busyEp}
            className="inline-flex items-center gap-2 rounded-full bg-magic px-5 py-2.5 font-semibold text-black hover:opacity-90 transition-opacity disabled:opacity-60">
            {busyEp ? <Spinner size={18} className="animate-spin" /> : <FilmSlate size={18} weight="fill" />}
            {busyEp ? "Writing full episode…" : "Generate Full 10-min Episode"}
          </button>
        </div>
        <p className="mt-2 text-xs text-zinc-500">Type or speak in any language (English, Türkçe, Български…). API cost: cloud (Universal Key). Real Claude generation, not mock.</p>
      </Panel>

      {/* ===== Storage-First Smart Production ===== */}
      <Panel className="mb-8 border-[#00E5FF]/20" data-testid="storage-first-panel">
        <div className="flex items-center gap-2 mb-1">
          <Recycle size={20} weight="fill" className="text-[#00FF9D]" />
          <h3 className="font-display text-lg font-bold text-white">Storage-First Smart Production</h3>
          <Tag>REUSE → ADAPT → LOCAL → AI</Tag>
        </div>
        <p className="text-xs text-zinc-500 mb-4">Tell Studio AI how to produce. It searches your real Asset Library first and only plans new paid AI for genuinely missing shots. Nothing paid runs without your approval.</p>
        <textarea data-testid="storage-instruction" value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={2}
          className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3 text-sm text-white outline-none focus:border-[#00FF9D]/50 mb-3"
          placeholder='e.g. "Use my storage first. Only generate the 3 missing shots. Max new AI budget £5. No premium video."' />
        <button data-testid="storage-plan-btn" onClick={planStorageFirst} disabled={planning}
          className="inline-flex items-center gap-2 rounded-full bg-[#00FF9D]/15 border border-[#00FF9D]/40 text-[#00FF9D] px-5 py-2.5 font-semibold hover:bg-[#00FF9D]/25 transition-colors disabled:opacity-60">
          {planning ? <Spinner size={18} className="animate-spin" /> : <Recycle size={18} weight="fill" />}
          {planning ? "Analysing your storage…" : "Plan (Storage-First)"}
        </button>

        {plan && (
          <div className="mt-5" data-testid="storage-plan-result">
            <div className="mb-3">
              <div className="text-sm text-zinc-400">New episode plan</div>
              <div className="font-display text-lg font-bold text-magic">{plan.episode?.title}</div>
              <p className="text-sm text-zinc-300 mt-1">{plan.episode?.logline || plan.episode?.synopsis}</p>
            </div>
            {/* reuse bars */}
            <div className="rounded-xl bg-black/30 border border-white/10 p-4 mb-4">
              <div className="flex h-3 w-full overflow-hidden rounded-full mb-2">
                <div className="bg-[#00FF9D]" style={{ width: `${plan.gap.reuse_pct}%` }} title="Reused" />
                <div className="bg-[#00E5FF]" style={{ width: `${plan.gap.adapt_pct}%` }} title="Adapted" />
                <div className="bg-[#FF007F]" style={{ width: `${plan.gap.new_pct}%` }} title="New" />
              </div>
              <div className="flex flex-wrap gap-4 text-xs">
                <span className="text-[#00FF9D]">● Reused {plan.gap.reuse_pct}%</span>
                <span className="text-[#00E5FF]">● Adapted {plan.gap.adapt_pct}%</span>
                <span className="text-[#FF007F]">● New {plan.gap.new_pct}%</span>
                <span className="ml-auto text-white font-semibold" data-testid="storage-cost">Est. paid AI: £{plan.gap.estimated_paid_gbp?.toFixed(2)}</span>
              </div>
              <div className="mt-2 text-xs text-zinc-500">
                New AI video shots: {plan.gap.new_ai_video_shots} · Premium video: {plan.gap.premium_video_allowed ? "allowed" : "OFF (free preview)"}
                {plan.gap.budget_cap_gbp != null && ` · Budget cap £${plan.gap.budget_cap_gbp} (${plan.gap.within_budget ? "within" : "OVER"})`}
              </div>
            </div>
            {/* buckets */}
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              {[["AVAILABLE", plan.gap.available, "#00FF9D"], ["ADAPTABLE", plan.gap.adaptable, "#00E5FF"], ["MISSING", plan.gap.missing, "#FF007F"]].map(([label, items, color]) => (
                <div key={label} className="rounded-xl bg-black/20 border border-white/10 p-3">
                  <div className="text-xs font-semibold mb-2" style={{ color }}>{label} ({items.length})</div>
                  <ul className="space-y-1">
                    {(items || []).slice(0, 10).map((it, i) => (
                      <li key={i} className="text-xs text-zinc-300">
                        <span className="text-zinc-500">[{it.type}]</span> {it.name}
                        <span className="text-zinc-600"> · {it.how}{it.provider ? ` · ${it.provider}` : ""}</span>
                      </li>
                    ))}
                    {items.length === 0 && <li className="text-xs text-zinc-600">— none —</li>}
                  </ul>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <button data-testid="storage-execute-btn" onClick={executePlan} disabled={executing}
                className="inline-flex items-center gap-2 rounded-full bg-magic px-5 py-2.5 font-semibold text-black hover:opacity-90 transition-opacity disabled:opacity-60">
                {executing ? <Spinner size={18} className="animate-spin" /> : <FilmSlate size={18} weight="fill" />}
                {executing ? "Producing…" : "Produce (reuse + free only)"}
              </button>
              {plan.gap.missing.length > 0 && (
                <span className="text-xs text-[#FFB800]">⚠ {plan.gap.missing.length} paid AI item(s) (£{plan.gap.estimated_paid_gbp?.toFixed(2)}) need separate approval — not generated here.</span>
              )}
            </div>
          </div>
        )}
      </Panel>

      {episode && (
        <div className="mb-10" data-testid="episode-result">
          <h3 className="font-display text-lg font-bold mb-4">Full Episode — <span className="text-magic">{episode.title}</span></h3>
          <Panel className="mb-4">
            <p className="text-zinc-300">{episode.synopsis}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {episode.learning_objective && <Tag>🎓 {episode.learning_objective}</Tag>}
              {episode.emotional_objective && <Tag>💫 {episode.emotional_objective}</Tag>}
              {episode.music_direction && <Tag>🎵 {episode.music_direction}</Tag>}
            </div>
          </Panel>
          <div className="space-y-3">
            {(episode.scenes || []).map((s, i) => (
              <Panel key={i} data-testid={`gen-scene-${i}`} className="flex flex-col sm:flex-row gap-4">
                <div className="sm:w-10 shrink-0 font-mono text-[#00E5FF] text-sm">SC{s.n || i + 1}</div>
                <div className="flex-1">
                  <div className="text-sm text-white font-medium">{s.location} {s.duration_sec ? `· ${s.duration_sec}s` : ""}</div>
                  <p className="text-sm text-zinc-400 mt-1">{s.action}</p>
                  {s.dialogue && <p className="text-sm text-zinc-200 mt-2 italic">"{s.dialogue}"</p>}
                  <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-zinc-400">
                    {s.emotion && <Tag className="text-[10px]">{s.emotion}</Tag>}
                    {s.camera && <span className="flex items-center gap-1"><VideoCamera size={12} weight="duotone" className="text-[#00E5FF]" />{s.camera}</span>}
                    {s.music && <span className="flex items-center gap-1"><MusicNote size={12} weight="duotone" className="text-[#00FF9D]" />{s.music}</span>}
                  </div>
                </div>
              </Panel>
            ))}
          </div>
          {episode.shorts_opportunities?.length > 0 && (
            <Panel className="mt-4">
              <div className="text-sm text-zinc-400 mb-2">Shorts opportunities</div>
              <div className="flex flex-wrap gap-2">{episode.shorts_opportunities.map((s, i) => <Tag key={i}>{s}</Tag>)}</div>
            </Panel>
          )}
        </div>
      )}

      {concepts.length > 0 && (
        <div className="mb-10">
          <h3 className="font-display text-lg font-bold mb-4">Generated Concepts</h3>
          <div className="grid lg:grid-cols-3 gap-5">
            {concepts.map((c, i) => (
              <Panel key={i} hover data-testid={`concept-${i}`} className="flex flex-col">
                <h4 className="font-display text-xl font-bold text-white mb-2">{c.title}</h4>
                <p className="text-sm text-zinc-300 flex-1">{c.synopsis}</p>
                <div className="mt-4 space-y-2 text-xs">
                  <div className="flex items-center gap-2 text-zinc-400"><GraduationCap size={15} weight="duotone" className="text-[#00FF9D]" /> {c.learning}</div>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1 text-zinc-400"><SmileyWink size={14} weight="duotone" className="text-[#FFB800]" /> Comedy {c.comedy}/5</span>
                    <span className="flex items-center gap-1 text-zinc-400"><Compass size={14} weight="duotone" className="text-[#00E5FF]" /> Adventure {c.adventure}/5</span>
                  </div>
                </div>
                <button onClick={() => toast.success("Concept selected → sent to Episode Studio")}
                  className="mt-4 rounded-full bg-white/5 border border-white/10 py-2 text-sm text-white hover:border-[#00E5FF]/50 transition-colors">Develop into Episode</button>
              </Panel>
            ))}
          </div>
        </div>
      )}

      <h3 className="font-display text-lg font-bold mb-4">Saved Stories</h3>
      {loading ? <Loading /> : (
        <div className="grid sm:grid-cols-2 gap-5">
          {(existing || []).map((s) => (
            <Panel key={s.id} hover>
              <div className="flex items-start justify-between">
                <h4 className="font-display text-lg font-bold text-white">{s.title}</h4>
                <Tag>{s.age}</Tag>
              </div>
              <p className="text-sm text-zinc-400 mt-1">{s.logline}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Tag>{s.learning}</Tag><Tag>{s.duration}</Tag><Tag>{s.world}</Tag>
              </div>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
